<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
    :sm="sm"
  >
    <v-row dense>
      <v-col
        :class="labelClass"
        cols="12"
      >
        {{ $bct(label) }}
        <RequiredIndicator v-if="mandatory" />

        <slot name="label-icon">
          <HelpIcon
            :document="helpDocument"
            :on-help-click="openHelpFlyout"
          />
          <v-tooltip
            v-if="tooltipText"
            :location="tooltipLocation"
          >
            <template #activator="tooltipOptions">
              <span>
                <v-icon
                  v-bind="tooltipOptions.props"
                  :color="tooltipColor"
                  :icon="tooltipIcon"
                  class="d-inline ps-1 pt-0 pb-1 material-icons-outlined"
                />
              </span>
            </template>

            <span>
              {{ $bct(tooltipText) }}
            </span>
          </v-tooltip>
        </slot>
      </v-col>

      <template v-if="description">
        <v-col
          class="labeled-autocomplete-description fs-13 c-light-black text-wrap mt-0 mb-1 pt-0 pb-0"
          cols="12"
          data-testid="description"
        >
          <span>
            {{ $bct(description) }}
          </span>
        </v-col>
      </template>

      <v-col>
        <v-autocomplete
          v-model="localValue"
          @blur="handleBlur"
          @update:model-value="handleUpdateModelValue"
          @update:search="$emit('update:search', $event)"
          @vue:mounted="$emit('update:search', '')"
          :append-inner-icon="appendIcon"
          :aria-label="ariaLabel"
          :chips="chips || undefined"
          :closable-chips="deletableChips || undefined"
          v-bind="filteredAttrs"
          :close-text="label"
          :data-testid="inputTestId || 'labeled-select'"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked || undefined"
          :item-subtitle="itemSubtitle"
          :item-title="itemTitle"
          :item-value="itemValue"
          :items="items || selectableItems"
          :loading="loading || undefined"
          :open-text="label"
          data-cy="labeled-select"
          variant="filled"
          hide-details
          tile
        />
      </v-col>
    </v-row>

    <v-divider
      v-if="dividedBottom"
      class="mt-4 mb-2"
    />
  </v-col>
</template>

<script setup>
import { onUpdated } from 'vue';

import attrsOmitDataPrefix from '@/shared/utils/filteredAttrs';
import HelpIcon from '@/shared/components/form/HelpIcon.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const localValue = defineModel({ type: undefined });

const props = defineProps({
  chips: {
    type: Boolean,
    default: undefined,
  },
  deletableChips: {
    type: Boolean,
    default: undefined,
  },
  dividedBottom: {
    type: Boolean,
    default: undefined,
  },
  inputTestId: {
    type: String,
    default: null,
  },
  items: {
    type: Array,
    default: undefined,
  },
  itemSubtitle: {
    type: [String, Function],
    default: undefined,
  },
  itemTitle: {
    type: [String, Function],
    default: 'text',
  },
  itemValue: {
    type: String,
    default: 'value',
  },
  loading: {
    type: Boolean,
    default: undefined,
  },
  tooltipColor: {
    type: String,
    default: null,
  },
  tooltipIcon: {
    type: String,
    default: 'info',
  },
  tooltipLocation: {
    type: String,
    default: 'bottom',
  },
  tooltipText: {
    type: String,
    default: null,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const attrs = useAttrs();
const $bct = inject('translator');
const emit = defineEmits(['update:search'].concat(useLabeledField.emits));
defineExpose({ emit }); // Emit is used in composable, this is included for linter

const filteredAttrs = ref(attrsOmitDataPrefix(attrs));

const { appendIcon, labelClass, handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);
const { ariaLabel, enabled, helpDocument, label, locked, openHelpFlyout, propertyBySchema } =
  useSchematizedField(...propsToRefs(props, useSchematizedField.paramKeys));

const selectableItems = computed(() => {
  if (props.itemTitle !== 'text' || props.itemValue !== 'value') {
    return props.items;
  }

  if (props.items && props.items.length > 0) {
    return props.items;
  }

  const schemaItems = propertyBySchema.value?.enum || propertyBySchema.value?.items?.enum || [];

  if (schemaItems[0]?.text || schemaItems[0]?.value) {
    return schemaItems;
  }

  return schemaItems.map((item) => ({ text: item, value: item }));
});

onUpdated(() => {
  filteredAttrs.value = attrsOmitDataPrefix(attrs);
});
</script>

<style scoped>
.labeled-autocomplete-description span {
  display: block;
  line-height: 1.35;
}
</style>
